import makeRequest, { REQUEST_TYPES } from '../../utils/request'

interface SecureApiTokenResponse {
    apiKey: string
}

/** How long a minted key is reused before requesting a fresh one. Well under
 * the server-side validUntil so a cached key never expires mid-use. */
const KEY_REUSE_MS = 60 * 60 * 1000

const keyCache = new Map<
    string,
    { token: SecureApiTokenResponse; mintedAt: number }
>()

const ObservabilityService = {
    /**
     * Returns a short-lived secured Algolia key for the integration. Keys are
     * cached in memory per integration and reused for up to an hour, so
     * remounting observability panels does not accumulate freshly minted keys.
     *
     * @param integrationId - The integration to mint the key for.
     * @returns The secured key response.
     */
    async fetchSecureApiToken(
        integrationId: string
    ): Promise<SecureApiTokenResponse> {
        const cached = keyCache.get(integrationId)
        if (cached && Date.now() - cached.mintedAt < KEY_REUSE_MS) {
            return cached.token
        }
        const token: SecureApiTokenResponse = await makeRequest(
            `/api/v1/integrations/${integrationId}/secure-api-key`,
            REQUEST_TYPES.get
        )
        if (typeof token?.apiKey !== 'string' || token.apiKey === '') {
            throw new Error('No API key returned for indexing activity.')
        }
        keyCache.set(integrationId, { token, mintedAt: Date.now() })
        return token
    },
}

export default ObservabilityService
